import { useCallback, useMemo, useState } from 'react'

import { Input } from '../../../Components'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { showToast } from '../../../utils/toast'
import { CLIENT_CONFIGS } from '../constants'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * Barra di selezione configurazione client per precaricare i parametri di test.
 * Per i client con testUsers (es. Goelett), mostra un campo email per ciascun ruolo
 * (Self-booker, Arranger, Approver) con le opzioni precaricate.
 * @param {Object} props
 * @param {Function} props.onLoadClientConfig - Callback per applicare la config client allo state
 * @param {Function} props.resetConversation - Resetta la conversazione corrente
 */
export function ClientConfigBar({ onLoadClientConfig, resetConversation }) {
  const [selectedClient, setSelectedClient] = useState(null)
  const [roleEmails, setRoleEmails] = useState({
    selfBooker: null,
    arranger: null,
    approver: null
  })

  const clientOptions = CLIENT_CONFIGS.map(({ id, label }) => ({ label, value: id }))

  const selectedConfig = useMemo(
    () => CLIENT_CONFIGS.find((c) => c.id === selectedClient),
    [selectedClient]
  )

  const optionsByRole = useMemo(() => {
    if (!selectedConfig?.testUsers?.length) return null

    const selfBookers = selectedConfig.testUsers
      .filter((u) => u.role === 'Self-booker')
      .map(({ email, note }) => ({
        label: `${email}${note ? ` ★ ${note}` : ''}`,
        value: email
      }))

    const arrangers = selectedConfig.testUsers
      .filter((u) => u.role === 'Arranger')
      .map(({ email, note }) => ({
        label: `${email}${note ? ` ★ ${note}` : ''}`,
        value: email
      }))

    const approvers = selectedConfig.testUsers
      .filter((u) => u.role === 'Approver')
      .map(({ email, note }) => ({
        label: `${email}${note ? ` ★ ${note}` : ''}`,
        value: email
      }))

    return { selfBookers, arrangers, approvers }
  }, [selectedConfig])

  const handleClientChange = useCallback(({ clientConfig }) => {
    setSelectedClient(clientConfig)
    const cfg = CLIENT_CONFIGS.find((c) => c.id === clientConfig)
    if (cfg?.testUsers?.length) {
      const firstSelf =
        cfg.testUsers.find((u) => u.role === 'Self-booker' && u.note)?.email ||
        cfg.testUsers.find((u) => u.role === 'Self-booker')?.email ||
        null
      const firstArr = cfg.testUsers.find((u) => u.role === 'Arranger')?.email || null
      const firstApp = cfg.testUsers.find((u) => u.role === 'Approver')?.email || null
      setRoleEmails({ selfBooker: firstSelf, arranger: firstArr, approver: firstApp })
    } else {
      setRoleEmails({ selfBooker: null, arranger: null, approver: null })
    }
  }, [])

  const handleLoad = useCallback(() => {
    if (!selectedClient || !selectedConfig) return

    const emailOverride = roleEmails.selfBooker || selectedConfig.userEmail
    onLoadClientConfig({
      ...selectedConfig,
      userEmail: emailOverride,
      goelettRoleEmails: optionsByRole
        ? {
            selfBooker: roleEmails.selfBooker,
            arranger: roleEmails.arranger,
            approver: roleEmails.approver
          }
        : undefined
    })
    resetConversation()
    showToast('success', t.dbcClientLoaded || 'Config client caricata', selectedConfig.label)
  }, [
    selectedClient,
    selectedConfig,
    roleEmails,
    optionsByRole,
    onLoadClientConfig,
    resetConversation
  ])

  return (
    <div className="debug-preset-bar debug-preset-bar--stacked">
      <div className="debug-preset-bar__group" style={{ flex: 1, flexWrap: 'wrap', gap: 8 }}>
        <i
          className="pi pi-briefcase"
          style={{ fontSize: 14, color: 'var(--ink-500)', flexShrink: 0 }}
        />
        <Input
          id="clientConfig"
          dropdown
          options={clientOptions}
          value={selectedClient}
          placeholder={t.dbcSelectClient || 'Flusso cliente...'}
          showClear
          style={{ fontSize: 13, width: '100%', borderRadius: 8, maxWidth: 220 }}
          onChange={handleClientChange}
        />

        {selectedConfig && (
          <>
            <span className="nbadge nbadge--info" style={{ fontSize: 11 }}>
              {selectedConfig.typeEndpointCall}
            </span>
            <span style={{ fontSize: 12, color: 'var(--ink-400)' }}>
              {selectedConfig.description} ({selectedConfig.tomcatCode}/{selectedConfig.codSociety})
            </span>
          </>
        )}

        {selectedClient && (
          <button
            className="debug-action-btn debug-action-btn--primary"
            onClick={handleLoad}
            title={t.dbcLoadClient || 'Carica Configurazione'}
          >
            <i className="pi pi-upload" style={{ fontSize: 12 }} />
            <span className="preset-btn-label">{t.dbcLoadClient || 'Applica'}</span>
          </button>
        )}
      </div>

      {optionsByRole && (
        <div className="debug-role-emails">
          {optionsByRole.selfBookers.length > 0 && (
            <div className="nfield" style={{ minWidth: 240 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-600)' }}>
                Self-booker
              </label>
              <Input
                id="selfBookerEmail"
                dropdown
                options={optionsByRole.selfBookers}
                value={roleEmails.selfBooker}
                placeholder="Seleziona Self-booker..."
                showClear
                style={{ fontSize: 12, width: '100%', borderRadius: 8 }}
                onChange={({ selfBookerEmail }) =>
                  setRoleEmails((prev) => ({ ...prev, selfBooker: selfBookerEmail }))
                }
              />
            </div>
          )}

          {optionsByRole.arrangers.length > 0 && (
            <div className="nfield" style={{ minWidth: 240 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-600)' }}>
                Arranger
              </label>
              <Input
                id="arrangerEmail"
                dropdown
                options={optionsByRole.arrangers}
                value={roleEmails.arranger}
                placeholder="Seleziona Arranger..."
                showClear
                style={{ fontSize: 12, width: '100%', borderRadius: 8 }}
                onChange={({ arrangerEmail }) =>
                  setRoleEmails((prev) => ({ ...prev, arranger: arrangerEmail }))
                }
              />
            </div>
          )}

          {optionsByRole.approvers.length > 0 && (
            <div className="nfield" style={{ minWidth: 240 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-600)' }}>
                Approver
              </label>
              <Input
                id="approverEmail"
                dropdown
                options={optionsByRole.approvers}
                value={roleEmails.approver}
                placeholder="Seleziona Approver..."
                showClear
                style={{ fontSize: 12, width: '100%', borderRadius: 8 }}
                onChange={({ approverEmail }) =>
                  setRoleEmails((prev) => ({ ...prev, approver: approverEmail }))
                }
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
